(() => {
  const atlas = document.querySelector('[data-ew-atlas-prototype]');
  if (!atlas) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fieldNav = atlas.querySelector('[data-field-nav]');
  const navLinks = fieldNav ? [...fieldNav.querySelectorAll('a[href^="#"]')] : [];

  navLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      const target = atlas.querySelector(link.getAttribute('href'));
      if (!target || reducedMotion) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', link.getAttribute('href'));
    });
  });

  if ('IntersectionObserver' in window && navLinks.length) {
    const byId = new Map(navLinks.map((link) => [link.getAttribute('href').slice(1), link]));
    const sections = [...byId.keys()]
      .map((id) => atlas.querySelector(`#${id}`))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        navLinks.forEach((link) => link.removeAttribute('aria-current'));
        byId.get(visible.target.id)?.setAttribute('aria-current', 'location');
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: [0, 0.15, 0.5] }
    );

    sections.forEach((section) => observer.observe(section));
  }

  const mapDialog = atlas.querySelector('[data-map-dialog]');
  const mapOpen = atlas.querySelector('[data-map-open]');
  const mapClose = atlas.querySelector('[data-map-close]');

  const closeMap = () => {
    if (mapDialog?.open) mapDialog.close();
  };

  mapOpen?.addEventListener('click', () => {
    if (!mapDialog) return;
    const iframe = mapDialog.querySelector('iframe[data-src]');
    if (iframe && !iframe.src) iframe.src = iframe.dataset.src;
    if (typeof mapDialog.showModal === 'function') {
      mapDialog.showModal();
    } else {
      mapDialog.setAttribute('open', '');
    }
  });

  mapClose?.addEventListener('click', closeMap);
  mapDialog?.addEventListener('click', (event) => {
    if (event.target === mapDialog) closeMap();
  });

})();

document.querySelectorAll('[data-weather-module]').forEach((weather) => {
  const isBoatWeather = weather.dataset.weatherContext === 'boat';
  const weatherEndpoint = weather?.dataset.weatherEndpoint;
  const weatherSiteId = weather?.dataset.weatherSiteId;
  const weatherLocationLabel = weather?.dataset.weatherLocationLabel;
  const weatherStateLabel = weather?.querySelector('[data-weather-state-label]');
  const weatherTemperature = weather?.querySelector('[data-weather-temperature]');
  const weatherCondition = weather?.querySelector('[data-weather-condition]');
  const weatherWindDirection = weather?.querySelector('[data-weather-wind-direction]');
  const weatherWindSpeed = weather?.querySelector('[data-weather-wind-speed]');
  const weatherGust = weather?.querySelector('[data-weather-gust]');
  const weatherRain = weather?.querySelector('[data-weather-rain]');
  const weatherUpdated = weather?.querySelector('[data-weather-updated]');
  const weatherMessage = weather?.querySelector('[data-weather-message]');
  const weatherRefresh = weather?.querySelector('[data-weather-refresh]');

  const formatNumber = (value, maximumFractionDigits = 1) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return new Intl.NumberFormat('en-AU', { maximumFractionDigits }).format(value);
  };

  const formatObservationTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  };

  const setText = (element, text) => {
    if (element) element.textContent = text;
  };

  const clearWeatherValues = () => {
    setText(weatherTemperature, '—');
    setText(weatherCondition, 'Live weather temporarily unavailable');
    setText(weatherWindDirection, '—');
    setText(weatherWindSpeed, '—');
    setText(weatherGust, 'Gust unavailable');
    setText(weatherRain, '—');
    setText(weatherUpdated, 'No current observation available');
  };

  const setWeatherState = (state, label, message) => {
    if (!weather) return;
    weather.dataset.weatherState = state;
    weather.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    setText(weatherStateLabel, label);
    setText(weatherMessage, message);
  };

  const renderWeatherFailure = (state) => {
    clearWeatherValues();
    if (state === 'error') {
      setWeatherState('error', 'Error', isBoatWeather ? 'Weather data could not be validated. Check official weather information.' : 'Weather data could not be validated. Use the official forecast and Port Phillip waters links.');
      return;
    }
    setWeatherState('unavailable', 'Unavailable', isBoatWeather ? 'Live weather temporarily unavailable. Check official weather information.' : 'Live weather temporarily unavailable. Use the official forecast and Port Phillip waters links.');
  };

  const renderWeather = (payload) => {
    if (
      !payload ||
      !weatherSiteId ||
      !weatherLocationLabel ||
      payload.site_id !== weatherSiteId ||
      payload.public_location_label !== weatherLocationLabel ||
      payload.provider !== 'WeatherAPI.com' ||
      !['current', 'stale'].includes(payload.freshness_state) ||
      !payload.current
    ) {
      throw new Error('Invalid weather response');
    }

    const temperature = formatNumber(payload.current.temperature_c);
    const windSpeed = formatNumber(payload.current.wind_speed_kmh);
    const windGust = formatNumber(payload.current.wind_gust_kmh);
    const rain = formatNumber(payload.current.precip_mm, 2);
    const observationTime = formatObservationTime(payload.observation_time || payload.provider_observation_time);

    setText(weatherTemperature, temperature === null ? 'Unavailable' : `${temperature}°C`);
    setText(weatherCondition, payload.current.condition || 'Condition unavailable');
    setText(weatherWindDirection, payload.current.wind_direction || 'Direction unavailable');
    setText(weatherWindSpeed, windSpeed === null ? 'Speed unavailable' : `${windSpeed} km/h`);
    setText(weatherGust, windGust === null ? 'Gust unavailable' : `Gust ${windGust} km/h`);
    setText(weatherRain, rain === null ? 'Unavailable' : `${rain} mm`);
    setText(weatherUpdated, observationTime ? `Observed ${observationTime}` : 'Observation time unavailable');

    if (payload.freshness_state === 'stale') {
      setWeatherState('stale', 'Stale', 'Weather data may be out of date. Check the timestamp and official sources.');
    } else {
      setWeatherState('current', 'Current', isBoatWeather ? 'WeatherAPI atmospheric observation near this site.' : 'WeatherAPI locality observation. Environmental data only—not a dive verdict.');
    }
  };

  const loadWeather = async () => {
    if (!weather || !weatherEndpoint) return;
    setWeatherState('loading', 'Loading', 'Retrieving live weather…');
    if (weatherRefresh) weatherRefresh.disabled = true;

    try {
      let response;
      try {
        response = await fetch(weatherEndpoint, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      } catch (error) {
        renderWeatherFailure('unavailable');
        return;
      }

      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        renderWeatherFailure('error');
        return;
      }

      if (!response.ok) {
        renderWeatherFailure(payload?.freshness_state === 'error' ? 'error' : 'unavailable');
        return;
      }

      try {
        renderWeather(payload);
      } catch (error) {
        renderWeatherFailure('error');
      }
    } finally {
      if (weatherRefresh) weatherRefresh.disabled = false;
    }
  };

  weatherRefresh?.addEventListener('click', loadWeather);
  loadWeather();
});
